/**
 * Read models over a `PatientState`: the numbers the dashboard, the practice list, the coach prompt
 * and the triage flags all show. Pure; `now` is always an argument.
 */

import {
  BP_DROP_SYSTOLIC,
  BP_LOW,
  FLAG_WINDOW_MS,
  PULSE_HIGH,
  PULSE_LOW,
  PULSE_VERY_LOW,
  WEIGHT_GAIN_DAY_LBS,
  WEIGHT_GAIN_WEEK_LBS,
} from './thresholds.js';
import { dayKey, daysBetween, lastDays, localHour, shiftDay, weekdayShort } from './time.js';
import type { BpReading, CareGoal, EventSource, Medication, MetricDay, MetricKind, PatientState, Symptom, SymptomReport } from './types.js';

export type BpClass = 'low' | 'optimal' | 'elevated' | 'stage-1' | 'stage-2' | 'crisis';

export const BP_CLASS_LABEL: Readonly<Record<BpClass, string>> = {
  low: 'Low',
  optimal: 'Optimal',
  elevated: 'Elevated',
  'stage-1': 'High Stage 1',
  'stage-2': 'High Stage 2',
  crisis: 'Hypertensive crisis',
};

/**
 * ACC/AHA bands, with a low band below them. Cardiac patients are titrated up on medications that
 * lower blood pressure, so "too low" has to be sayable: before this band existed, a reading of
 * 88/54 on a newly increased beta blocker was reported to the patient as Optimal.
 */
export function classifyBp(systolic: number, diastolic: number): BpClass {
  if (systolic < BP_LOW.systolic || diastolic < BP_LOW.diastolic) return 'low';
  if (systolic >= 180 || diastolic >= 120) return 'crisis';
  if (systolic >= 140 || diastolic >= 90) return 'stage-2';
  if (systolic >= 130 || diastolic >= 80) return 'stage-1';
  if (systolic >= 120) return 'elevated';
  return 'optimal';
}

export function latestReading(state: PatientState): BpReading | null {
  return state.readings.length > 0 ? (state.readings[state.readings.length - 1] as BpReading) : null;
}

/** Average of the readings in the last 7 days, rounded. Null with no readings. */
export function weeklyBpAverage(state: PatientState, now: number): { systolic: number; diastolic: number; count: number } | null {
  const days = new Set(lastDays(dayKey(now, state.profile.tzOffsetMinutes), 7));
  const rs = state.readings.filter((r) => days.has(r.dayKey));
  if (rs.length === 0) return null;
  const s = rs.reduce((a, r) => a + r.systolic, 0);
  const d = rs.reduce((a, r) => a + r.diastolic, 0);
  return { systolic: Math.round(s / rs.length), diastolic: Math.round(d / rs.length), count: rs.length };
}

/** The last 7 days, one entry each, with the latest reading that day (or null). Oldest first. */
export function bpTrend(state: PatientState, now: number): Array<{ dayKey: string; label: string; reading: BpReading | null }> {
  return lastDays(dayKey(now, state.profile.tzOffsetMinutes), 7).map((k) => {
    const same = state.readings.filter((r) => r.dayKey === k);
    return { dayKey: k, label: weekdayShort(k), reading: same.length > 0 ? (same[same.length - 1] as BpReading) : null };
  });
}

/** Change in systolic between this week's average and last week's. Negative is an improvement. */
export function systolicChange(state: PatientState, now: number): number | null {
  const tz = state.profile.tzOffsetMinutes;
  const today = dayKey(now, tz);
  const thisWeek = new Set(lastDays(today, 7));
  const lastWeek = new Set(lastDays(today, 14).slice(0, 7));
  const avg = (days: Set<string>): number | null => {
    const rs = state.readings.filter((r) => days.has(r.dayKey));
    return rs.length === 0 ? null : rs.reduce((a, r) => a + r.systolic, 0) / rs.length;
  };
  const a = avg(thisWeek);
  const b = avg(lastWeek);
  return a === null || b === null ? null : Math.round(a - b);
}

/**
 * How far the newest reading sits below this patient's own recent baseline. Positive is a fall.
 * A drop is only meaningful against the person, not the population: 112 is unremarkable unless
 * they have been running 140 all week on a dose that was just increased.
 */
export function systolicDropFromBaseline(state: PatientState, now: number): number | null {
  const latest = latestReading(state);
  if (!latest) return null;
  const days = new Set(lastDays(dayKey(now, state.profile.tzOffsetMinutes), 7));
  const base = state.readings.filter((r) => days.has(r.dayKey) && r.dayKey !== latest.dayKey);
  if (base.length < 2) return null;
  const mean = base.reduce((a, r) => a + r.systolic, 0) / base.length;
  return Math.round(mean - latest.systolic);
}

export interface WeeklyProgress {
  /** Readings logged in the last 7 days, and the 5-a-week target. */
  bp: { done: number; target: number; onTrack: boolean };
  steps: { daysComplete: number; target: number; todayCount: number; todayGoal: number | null };
  /** Doses taken over doses scheduled, last 7 days. */
  adherence: { percent: number; daysComplete: number; taken: number; scheduled: number };
}

export const BP_READINGS_PER_WEEK = 5;

export function weeklyProgress(state: PatientState, now: number): WeeklyProgress {
  const tz = state.profile.tzOffsetMinutes;
  const today = dayKey(now, tz);
  const days = lastDays(today, 7);
  const daySet = new Set(days);

  const bpDone = new Set(state.readings.filter((r) => daySet.has(r.dayKey)).map((r) => r.dayKey)).size;
  const stepGoal = state.goals.find((g): g is Extract<CareGoal, { kind: 'steps' }> => g.kind === 'steps') ?? null;
  const stepDays = stepGoal ? state.steps.filter((s) => daySet.has(s.dayKey) && s.count >= stepGoal.target).length : 0;
  const todayCount = state.steps.find((s) => s.dayKey === today)?.count ?? 0;

  let scheduled = 0;
  let taken = 0;
  let daysComplete = 0;
  for (const d of days) {
    // Days before enrollment are not missed doses; only days the plan existed are counted.
    if (d < state.profile.memberSince) continue;
    let dayScheduled = 0;
    let dayTaken = 0;
    for (const med of state.medications) {
      dayScheduled += med.times.length;
      dayTaken += state.takes.filter((t) => t.dayKey === d && t.medicationId === med.id && !t.undone).length;
    }
    // Today only counts the doses whose time has passed, so a morning check-in is not "missed" at 8am.
    if (d === today) {
      const hour = localHour(now, tz);
      dayScheduled = 0;
      dayTaken = 0;
      for (const med of state.medications) {
        med.times.forEach((t, slot) => {
          if (Number(t.slice(0, 2)) <= hour) {
            dayScheduled += 1;
            if (state.takes.some((x) => x.dayKey === d && x.medicationId === med.id && x.slot === slot && !x.undone)) dayTaken += 1;
          }
        });
      }
    }
    scheduled += dayScheduled;
    taken += dayTaken;
    if (dayScheduled > 0 && dayTaken === dayScheduled) daysComplete += 1;
  }
  const percent = scheduled === 0 ? 100 : Math.round((taken / scheduled) * 100);

  return {
    bp: { done: bpDone, target: BP_READINGS_PER_WEEK, onTrack: bpDone >= Math.min(BP_READINGS_PER_WEEK, days.indexOf(today) + 1) },
    steps: { daysComplete: stepDays, target: 7, todayCount, todayGoal: stepGoal?.target ?? null },
    adherence: { percent, daysComplete, taken, scheduled },
  };
}

export interface TodayDose {
  medication: Medication;
  slot: number;
  time: string;
  taken: boolean;
}

/** Every dose scheduled today, in clock order, with whether it has been taken. */
export function todayDoses(state: PatientState, now: number): TodayDose[] {
  const day = dayKey(now, state.profile.tzOffsetMinutes);
  const out: TodayDose[] = [];
  for (const med of state.medications) {
    med.times.forEach((time, slot) => {
      out.push({ medication: med, slot, time, taken: state.takes.some((t) => t.dayKey === day && t.medicationId === med.id && t.slot === slot && !t.undone) });
    });
  }
  return out.sort((a, b) => a.time.localeCompare(b.time));
}

/* ------------------------------------------------------------------ metrics */

/** The newest day held for a metric, or null. Rows are ordered by day, so the last match wins. */
export function metricDay(state: PatientState, kind: MetricKind): MetricDay | null {
  for (let i = state.metrics.length - 1; i >= 0; i--) {
    const m = state.metrics[i] as MetricDay;
    if (m.kind === kind) return m;
  }
  return null;
}

/** The last `n` days of one metric, oldest first, days without a value omitted. */
export function metricDays(state: PatientState, kind: MetricKind, now: number, n = 7): MetricDay[] {
  const days = new Set(lastDays(dayKey(now, state.profile.tzOffsetMinutes), n));
  return state.metrics.filter((m) => m.kind === kind && days.has(m.dayKey));
}

/** The most recent value of each metric kind. */
export function latestMetrics(state: PatientState): Partial<Record<MetricKind, { value: number; at: number }>> {
  const out: Partial<Record<MetricKind, { value: number; at: number }>> = {};
  for (const m of state.metrics) {
    const cur = out[m.kind];
    if (!cur || m.lastAt >= cur.at) out[m.kind] = { value: m.last, at: m.lastAt };
  }
  return out;
}

export interface WeightTrend {
  latest: number;
  at: number;
  /** Pounds gained since the previous weighed day, when that day is close enough to compare. */
  dayDeltaLbs: number | null;
  /** Pounds gained since the oldest weighed day in the week before. */
  weekDeltaLbs: number | null;
}

/**
 * The heart-failure signal. A weight is only a number; a weight against the last one is the
 * earliest warning a patient has that they are retaining fluid.
 */
export function weightTrend(state: PatientState): WeightTrend | null {
  const rows = state.metrics.filter((m) => m.kind === 'weight-lbs');
  const newest = rows[rows.length - 1];
  if (!newest) return null;
  const before = rows.filter((r) => r.dayKey < newest.dayKey);
  const prev = before[before.length - 1] ?? null;
  const weekStart = shiftDay(newest.dayKey, -7);
  const weekBase = before.find((r) => r.dayKey >= weekStart) ?? null;
  const round1 = (n: number) => Math.round(n * 10) / 10;
  return {
    latest: newest.last,
    at: newest.lastAt,
    dayDeltaLbs: prev && daysBetween(prev.dayKey, newest.dayKey) <= 2 ? round1(newest.last - prev.last) : null,
    weekDeltaLbs: weekBase ? round1(newest.last - weekBase.last) : null,
  };
}

export interface PulseReading {
  value: number;
  at: number;
  source: EventSource;
  /** Whether the patient took it themselves. A watch number is not a taken pulse. */
  manual: boolean;
}

export function latestPulse(state: PatientState): PulseReading | null {
  const row = metricDay(state, 'heart-rate');
  if (!row) return null;
  return { value: row.last, at: row.lastAt, source: row.source ?? 'manual', manual: row.manual === true };
}

/* ----------------------------------------------------------------- symptoms */

export interface ReportedSymptoms {
  at: number;
  symptoms: Symptom[];
  note?: string;
  /** Whether it came with a reading, which tells the care team how to read it. */
  withReading: boolean;
}

/** Everything the patient has said they felt lately, from reports and from readings alike. */
export function recentSymptoms(state: PatientState, now: number, windowMs = FLAG_WINDOW_MS): ReportedSymptoms[] {
  const out: ReportedSymptoms[] = [];
  for (const r of state.symptomReports as SymptomReport[]) {
    if (now - r.at > windowMs) continue;
    out.push({ at: r.at, symptoms: r.symptoms.filter((s) => s !== 'none'), ...(r.note ? { note: r.note } : {}), withReading: false });
  }
  for (const r of state.readings) {
    if (now - r.at > windowMs) continue;
    const symptoms = r.symptoms.filter((s) => s !== 'none');
    if (symptoms.length > 0) out.push({ at: r.at, symptoms, withReading: true });
  }
  return out.sort((a, b) => b.at - a.at);
}

/* -------------------------------------------------------------------- flags */

export type FlagLevel = 'urgent' | 'attention' | 'info';

export interface PatientFlag {
  level: FlagLevel;
  code:
    | 'urgent-symptom'
    | 'hf-symptoms'
    | 'symptom-report'
    | 'bp-crisis'
    | 'bp-stage-2'
    | 'bp-low'
    | 'bp-drop'
    | 'bradycardia'
    | 'tachycardia'
    | 'weight-gain'
    | 'missed-meds'
    | 'no-readings'
    | 'inactive';
  text: string;
  at: number;
}

const URGENT_SYMPTOMS: readonly Symptom[] = ['chest-tightness', 'shortness-of-breath'];
/** The fluid pair. Alone they are worth a look; with a weight jump they are a decompensation. */
const FLUID_SYMPTOMS: readonly Symptom[] = ['swelling', 'orthopnea'];
const ATTENTION_SYMPTOMS: readonly Symptom[] = ['irregular-heartbeat', 'heart-racing', 'dizziness'];

/**
 * What the care team should see about this patient right now. Ordered urgent → info. Every flag
 * names the fact behind it, because a flag without a reason is a flag nobody can clear.
 *
 * Two rules hold across all of them. Nothing older than 48 hours raises anything, because a care
 * team cannot act on last month. And no device-sourced number alone is urgent: a watch saying 38
 * bpm is a watch, not a bradycardia.
 */
export function flags(state: PatientState, now: number): PatientFlag[] {
  const out: PatientFlag[] = [];
  const tz = state.profile.tzOffsetMinutes;
  const today = dayKey(now, tz);
  const latest = latestReading(state);
  const fresh = (at: number) => now - at < FLAG_WINDOW_MS;
  const reported = recentSymptoms(state, now);
  const weight = weightTrend(state);
  const weightFlagged =
    !!weight && fresh(weight.at) && ((weight.dayDeltaLbs !== null && weight.dayDeltaLbs >= WEIGHT_GAIN_DAY_LBS) || (weight.weekDeltaLbs !== null && weight.weekDeltaLbs >= WEIGHT_GAIN_WEEK_LBS));

  const urgent = reported.filter((r) => r.symptoms.some((s) => URGENT_SYMPTOMS.includes(s)))[0];
  if (urgent) {
    const names = urgent.symptoms.filter((s) => URGENT_SYMPTOMS.includes(s)).map(symptomLabel);
    const withBp = urgent.withReading && latest ? ` with a ${latest.systolic}/${latest.diastolic} reading` : '';
    out.push({ level: 'urgent', code: 'urgent-symptom', text: `Reported ${names.join(' and ').toLowerCase()}${withBp}.`, at: urgent.at });
  }

  const fluid = reported.filter((r) => r.symptoms.some((s) => FLUID_SYMPTOMS.includes(s)))[0];
  if (fluid) {
    const names = fluid.symptoms.filter((s) => FLUID_SYMPTOMS.includes(s)).map(symptomLabel);
    out.push({
      level: weightFlagged ? 'urgent' : 'attention',
      code: 'hf-symptoms',
      text: weightFlagged
        ? `Reported ${names.join(' and ').toLowerCase()} alongside a weight gain of ${weight?.dayDeltaLbs ?? weight?.weekDeltaLbs} lb.`
        : `Reported ${names.join(' and ').toLowerCase()}.`,
      at: fluid.at,
    });
  }

  if (latest && fresh(latest.at)) {
    const cls = classifyBp(latest.systolic, latest.diastolic);
    if (cls === 'crisis') {
      out.push({ level: 'urgent', code: 'bp-crisis', text: `Latest reading ${latest.systolic}/${latest.diastolic} is in the crisis range.`, at: latest.at });
    } else if (cls === 'stage-2') {
      out.push({ level: 'attention', code: 'bp-stage-2', text: `Latest reading ${latest.systolic}/${latest.diastolic} is High Stage 2.`, at: latest.at });
    } else if (cls === 'low') {
      out.push({ level: 'attention', code: 'bp-low', text: `Latest reading ${latest.systolic}/${latest.diastolic} is below ${BP_LOW.systolic}/${BP_LOW.diastolic}.`, at: latest.at });
    }
    const drop = systolicDropFromBaseline(state, now);
    if (cls !== 'low' && drop !== null && drop >= BP_DROP_SYSTOLIC) {
      out.push({ level: 'attention', code: 'bp-drop', text: `Systolic is ${drop} points below this week's average of the days before.`, at: latest.at });
    }
  } else if (latest && classifyBp(latest.systolic, latest.diastolic) === 'crisis') {
    // A crisis reading stays visible past the window; nothing else does.
    out.push({ level: 'urgent', code: 'bp-crisis', text: `Last reading ${latest.systolic}/${latest.diastolic} was in the crisis range.`, at: latest.at });
  }

  const pulse = latestPulse(state);
  if (pulse && fresh(pulse.at)) {
    if (pulse.value < PULSE_VERY_LOW) {
      out.push({ level: pulse.manual ? 'urgent' : 'attention', code: 'bradycardia', text: `Resting pulse ${pulse.value} bpm.`, at: pulse.at });
    } else if (pulse.value < PULSE_LOW) {
      out.push({ level: 'attention', code: 'bradycardia', text: `Resting pulse ${pulse.value} bpm.`, at: pulse.at });
    } else if (pulse.value > PULSE_HIGH) {
      out.push({ level: 'attention', code: 'tachycardia', text: `Resting pulse ${pulse.value} bpm.`, at: pulse.at });
    }
  }

  if (weight && weightFlagged) {
    const day = weight.dayDeltaLbs !== null && weight.dayDeltaLbs >= WEIGHT_GAIN_DAY_LBS;
    out.push({
      level: 'attention',
      code: 'weight-gain',
      text: day ? `Weight up ${weight.dayDeltaLbs} lb since the day before, now ${weight.latest} lb.` : `Weight up ${weight.weekDeltaLbs} lb over the week, now ${weight.latest} lb.`,
      at: weight.at,
    });
  }

  const other = reported.filter((r) => r.symptoms.some((s) => ATTENTION_SYMPTOMS.includes(s)) && !r.symptoms.some((s) => URGENT_SYMPTOMS.includes(s) || FLUID_SYMPTOMS.includes(s)))[0];
  if (other) {
    const names = other.symptoms.filter((s) => ATTENTION_SYMPTOMS.includes(s)).map(symptomLabel);
    out.push({ level: 'attention', code: 'symptom-report', text: `Reported ${names.join(' and ').toLowerCase()}.`, at: other.at });
  }

  // Two consecutive full days with a missed dose, ending yesterday (today is still in progress).
  if (state.medications.length > 0) {
    const days = lastDays(today, 3).slice(0, 2).filter((d) => d >= state.profile.memberSince);
    const missed = days.filter((d) => !allDosesTakenOn(state, d));
    if (days.length === 2 && missed.length === 2) {
      out.push({ level: 'attention', code: 'missed-meds', text: 'Missed at least one dose on each of the last two days.', at: now });
    }
  }

  const sinceReading = latest ? Math.floor((now - latest.at) / 86_400_000) : null;
  if (state.readings.length === 0 && daysSince(state.profile.memberSince, today) >= 3) {
    out.push({ level: 'info', code: 'no-readings', text: 'No blood pressure reading logged since enrollment.', at: now });
  } else if (sinceReading !== null && sinceReading >= 5) {
    out.push({ level: 'info', code: 'no-readings', text: `No blood pressure reading for ${sinceReading} days.`, at: now });
  }
  return out;
}

function allDosesTakenOn(state: PatientState, day: string): boolean {
  for (const med of state.medications) {
    for (let slot = 0; slot < med.times.length; slot++) {
      if (!state.takes.some((t) => t.dayKey === day && t.medicationId === med.id && t.slot === slot && !t.undone)) return false;
    }
  }
  return true;
}

function daysSince(fromKey: string, toKey: string): number {
  const p = (k: string) => {
    const [y, m, d] = k.split('-').map(Number) as [number, number, number];
    return Date.UTC(y, m - 1, d);
  };
  return Math.round((p(toKey) - p(fromKey)) / 86_400_000);
}

export const SYMPTOM_LABEL: Readonly<Record<Symptom, string>> = {
  none: 'None',
  headache: 'Headache',
  'chest-tightness': 'Chest tightness',
  'shortness-of-breath': 'Shortness of breath',
  'heart-racing': 'Heart racing',
  swelling: 'Swollen ankles or legs',
  orthopnea: 'Breathless lying flat',
  'irregular-heartbeat': 'Irregular heartbeat',
  dizziness: 'Dizzy or lightheaded',
  fatigue: 'Unusually tired',
};

export function symptomLabel(s: Symptom): string {
  return SYMPTOM_LABEL[s];
}

/** Overall engagement for the practice list: 0..100 from adherence, readings and streak. */
export function engagementScore(state: PatientState, now: number): number {
  const p = weeklyProgress(state, now);
  const bp = Math.min(1, p.bp.done / p.bp.target);
  const adherence = p.adherence.percent / 100;
  const streak = Math.min(1, state.streak.current / 7);
  return Math.round((bp * 0.35 + adherence * 0.45 + streak * 0.2) * 100);
}
